import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Autocomplete, Checkbox,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import { apiUrl, handleApiError } from './utils/api';
import NoMatchingRecords from './components/NoMatchingRecords';
import { useConfirm } from './components/ConfirmProvider';
import { useNotify } from './components/NotificationProvider';
import CustomerMergeDialog from './components/CustomerMergeDialog';
import AppDialog from './components/AppDialog';
import { formatDate } from './utils/date';

// Canonical truck types (value) + display label. Must match RateCardService.normalizeTruckType.
const TRUCK_TYPES = [
  { value: 'BELLY', label: 'Belly' },
  { value: 'END', label: 'End Dump' },
  { value: 'FLAT', label: 'Flat Bed' },
  { value: 'TANDEM', label: 'Tandem' },
  { value: 'TRI', label: 'Tri Axle' },
  { value: 'QUADS', label: 'Quads' },
  { value: 'SUPER', label: 'Super Dumps' },
];
const typeLabel = (v) => (TRUCK_TYPES.find((t) => t.value === v)?.label || v || '—');

const base = `${apiUrl}/api/field-tickets`;

export default function FieldTickets() {
  const [user] = useAuthState(auth);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // ticket being edited (dialog open when set)
  const confirm = useConfirm();
  const notify = useNotify();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(base, { params: statusFilter ? { status: statusFilter } : {} });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load field tickets'));
    } finally {
      setLoading(false);
    }
  };

  // Reload when the status filter changes; drop any selection so a bulk update can't hit tickets that
  // are no longer visible.
  useEffect(() => {
    setSelected(new Set());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    let extracted = 0;
    const problems = [];
    for (const file of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.post(`${base}/scan`, toForm(file), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const r = res.data || {};
        extracted += r.ticketsExtracted || 0;
        // SUCCESS = nothing to report; PARTIAL/FAILED carry a reason to show the user.
        if (r.status && r.status !== 'SUCCESS' && r.message) {
          problems.push(`${r.fileName || file.name}: ${r.message}`);
        }
      } catch (err) {
        problems.push(`${file.name}: ${handleApiError(err, 'scan failed')}`);
      }
    }
    setUploading(false);
    if (extracted > 0) notify.success(`Extracted ${extracted} field ticket(s) — review & verify below.`);
    if (problems.length > 0) {
      notify.warning(`${problems.length} file/page(s) had problems — see details above.`);
      setError(problems.join('\n'));
    } else if (extracted === 0) {
      notify.warning('No tickets were extracted.');
    }
    setStatusFilter('PENDING');
    load();
  };

  const toForm = (file) => {
    const fd = new FormData();
    fd.append('file', file);
    if (user?.email) fd.append('email', user.email);
    return fd;
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${base}/${editing.id}`, { ...editing, email: user?.email });
      setEditing(null);
      notify.success('Field ticket updated');
      load();
    } catch (err) {
      notify.error(handleApiError(err, 'Save failed'));
    }
  };

  const verify = async (row) => {
    if (!row.billedTo || !row.truckType || !row.basis || !row.quantity) {
      notify.warning('Set Billed To, truck type, basis and quantity before verifying.');
      return;
    }
    try {
      await axios.post(`${base}/${row.id}/verify`, null, { params: { email: user?.email } });
      notify.success(`Ticket ${row.ticketNo || row.id} verified`);
      load();
    } catch (err) {
      notify.error(handleApiError(err, 'Verify failed'));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: 'Delete field ticket',
      message: `Delete field ticket ${row.ticketNo || row.id}? This cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await axios.delete(`${base}/${row.id}`);
      notify.success('Field ticket deleted');
      load();
    } catch (err) {
      notify.error(handleApiError(err, 'Delete failed'));
    }
  };

  const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  // ---- multi-select bulk update (set Billed To / Truck type / Basis on many tickets at once,
  // instead of opening and saving each ticket in the review dialog one by one) ----
  const [selected, setSelected] = useState(() => new Set()); // selected ticket ids
  const [bulkBilledTo, setBulkBilledTo] = useState('');
  const [bulkType, setBulkType] = useState('');
  const [bulkBasis, setBulkBasis] = useState('');
  const [applying, setApplying] = useState(false);

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSel = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSel = rows.some((r) => selected.has(r.id)) && !allSel;
  const toggleSelAll = () => setSelected(allSel ? new Set() : new Set(rows.map((r) => r.id)));
  const clearSel = () => { setSelected(new Set()); setBulkBilledTo(''); setBulkType(''); setBulkBasis(''); };

  // Billed-To suggestions for the bulk field: the customer names already on the loaded tickets.
  const billedToOptions = Array.from(new Set(rows.map((r) => r.billedTo).filter(Boolean)));

  const applyBulk = async () => {
    const patch = {};
    if (bulkBilledTo.trim()) patch.billedTo = bulkBilledTo.trim();
    if (bulkType) patch.truckType = bulkType;
    if (bulkBasis) patch.basis = bulkBasis;
    if (Object.keys(patch).length === 0) {
      notify.warning('Set at least one field (Billed To, Truck type or Basis) to apply.');
      return;
    }
    // Only tickets still present in the loaded list — a stale id would spread `undefined` and PUT a
    // body missing every other field, blanking the ticket. Guard against it.
    const byId = new Map(rows.map((r) => [r.id, r]));
    const ids = [...selected].filter((id) => byId.has(id));
    if (ids.length === 0) { clearSel(); return; }
    const ok = await confirm({
      title: 'Update tickets',
      message: `Apply ${Object.keys(patch).map((k) => (k === 'billedTo' ? 'Billed To' : k === 'truckType' ? 'Truck type' : 'Basis')).join(', ')} to ${ids.length} selected ticket(s)?`,
      confirmText: 'Update',
    });
    if (!ok) return;
    setApplying(true);
    // Re-use the existing per-ticket PUT (no new endpoint) — send the full row with the patched fields,
    // so untouched fields are preserved. Batched via allSettled, then reported like the bulk delete.
    const results = await Promise.allSettled(ids.map((id) =>
      axios.put(`${base}/${id}`, { ...byId.get(id), ...patch, email: user?.email })));
    setApplying(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) notify.success(`Updated ${ids.length} ticket(s).`);
    else notify.warning(`Updated ${ids.length - failed} of ${ids.length}; ${failed} failed.`);
    clearSel();
    load();
  };

  // ---- customer merge (shared dialog; surfaces the alias engine) ----
  const [mergeOpen, setMergeOpen] = useState(false);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Field Tickets (Ivy handwritten)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scan handwritten Ivy field tickets, verify the AI extraction, then invoice them per customer on the
        Field Ticket Invoice screen. Rates come from the Rate Cards screen (code <b>LOAD</b> or <b>HOUR</b>,
        by truck type).
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Button variant="contained" component="label" startIcon={<UploadFileIcon />} disabled={uploading}>
          Upload field tickets
          <input type="file" accept="image/*,application/pdf" hidden multiple onChange={onUpload} />
        </Button>
        {uploading && <CircularProgress size={22} />}
        <Button variant="outlined" startIcon={<CallMergeIcon />} onClick={() => setMergeOpen(true)}>
          Merge customers
        </Button>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(e, v) => { if (v !== null) setStatusFilter(v); }}
        >
          <ToggleButton value="PENDING">Pending review</ToggleButton>
          <ToggleButton value="VERIFIED">Verified</ToggleButton>
          <ToggleButton value="">All</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">({rows.length})</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}

      {!loading && rows.length === 0 && <NoMatchingRecords />}

      {selected.size > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#FFF8E1', borderColor: '#F5D68A' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{selected.size} selected</Typography>
            <Autocomplete
              freeSolo size="small" options={billedToOptions} value={bulkBilledTo}
              onChange={(e, v) => setBulkBilledTo(v || '')} onInputChange={(e, v) => setBulkBilledTo(v || '')}
              sx={{ minWidth: 200, flex: 1 }}
              renderInput={(p) => <TextField {...p} label="Set Billed To" placeholder="(keep)" />}
            />
            <TextField select size="small" label="Set Truck type" value={bulkType}
              onChange={(e) => setBulkType(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">(keep)</MenuItem>
              {TRUCK_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Set Basis" value={bulkBasis}
              onChange={(e) => setBulkBasis(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">(keep)</MenuItem>
              <MenuItem value="HOUR">Hourly</MenuItem>
              <MenuItem value="LOAD">Per load</MenuItem>
            </TextField>
            <Button variant="contained" disabled={applying} onClick={applyBulk} sx={{ whiteSpace: 'nowrap' }}>
              {applying ? 'Applying…' : 'Apply to selected'}
            </Button>
            <Button color="inherit" onClick={clearSel} sx={{ whiteSpace: 'nowrap' }}>Clear</Button>
          </Stack>
        </Paper>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={allSel} indeterminate={someSel} onChange={toggleSelAll} />
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Ticket #</TableCell>
                <TableCell>Truck</TableCell>
                <TableCell>Billed To</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Basis</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover selected={selected.has(r.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
                  </TableCell>
                  <TableCell>{formatDate(r.ticketDate) || '—'}</TableCell>
                  <TableCell>{r.ticketNo || '—'}</TableCell>
                  <TableCell>{r.truckNo || '—'}</TableCell>
                  <TableCell>{r.billedTo || '—'}</TableCell>
                  <TableCell>{typeLabel(r.truckType)}</TableCell>
                  <TableCell>{r.basis === 'HOUR' ? 'Hourly' : 'Per load'}</TableCell>
                  <TableCell align="right">{r.quantity ?? '—'}</TableCell>
                  <TableCell align="right">{r.rate != null ? `$${Number(r.rate).toFixed(2)}` : '—'}</TableCell>
                  <TableCell align="right">{r.amount != null ? `$${Number(r.amount).toFixed(2)}` : '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.reviewStatus}
                      color={r.reviewStatus === 'VERIFIED' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }} title={r.updatedBy || r.createdBy || ''}>
                    {(r.updatedBy || r.createdBy)
                      ? `${r.updatedBy || r.createdBy}${(r.updatedAt || r.createdAt) ? ` · ${r.updatedAt || r.createdAt}` : ''}`
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => setEditing(r)} title="Review / edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {r.reviewStatus !== 'VERIFIED' && (
                      <IconButton size="small" color="success" onClick={() => verify(r)} title="Verify">
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => remove(r)} title="Delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review / edit dialog: scan image alongside the editable extracted fields. */}
      <AppDialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="md">
        <DialogTitle>Review field ticket {editing?.ticketNo ? `#${editing.ticketNo}` : ''}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {editing.hasImage ? (
                  <img
                    src={`${base}/${editing.id}/image`}
                    alt="scan"
                    style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                ) : (
                  <Alert severity="info">No scan image stored.</Alert>
                )}
              </Box>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2}>
                  <TextField label="Ticket #" value={editing.ticketNo ?? ''} onChange={(e) => set('ticketNo', e.target.value)} fullWidth size="small" />
                  <TextField label="Truck #" value={editing.truckNo ?? ''} onChange={(e) => set('truckNo', e.target.value)} fullWidth size="small" />
                </Stack>
                <TextField label="Date" type="date" value={editing.ticketDate ?? ''} onChange={(e) => set('ticketDate', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size="small" />
                <TextField label="Billed To (customer)" value={editing.billedTo ?? ''} onChange={(e) => set('billedTo', e.target.value)} fullWidth size="small" />
                <Stack direction="row" spacing={2}>
                  <TextField label="Truck type" select value={editing.truckType ?? ''} onChange={(e) => set('truckType', e.target.value)} fullWidth size="small">
                    {TRUCK_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                  <TextField label="Basis" select value={editing.basis ?? 'LOAD'} onChange={(e) => set('basis', e.target.value)} fullWidth size="small">
                    <MenuItem value="HOUR">Hourly</MenuItem>
                    <MenuItem value="LOAD">Per load</MenuItem>
                  </TextField>
                  <TextField label={editing.basis === 'HOUR' ? 'Hours' : 'Loads'} type="number" value={editing.quantity ?? ''} onChange={(e) => set('quantity', e.target.value)} fullWidth size="small" />
                  {editing.basis === 'HOUR' && (
                    <TextField label="Loads" type="number" value={editing.loads ?? ''} onChange={(e) => set('loads', e.target.value)} fullWidth size="small"
                      helperText="Load count (for the WB hourly invoice)" />
                  )}
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField label="From (producer)" value={editing.producerLocation ?? ''} onChange={(e) => set('producerLocation', e.target.value)} fullWidth size="small" />
                  <TextField label="To (destination)" value={editing.destination ?? ''} onChange={(e) => set('destination', e.target.value)} fullWidth size="small" />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField label="Driver" value={editing.driver ?? ''} onChange={(e) => set('driver', e.target.value)} fullWidth size="small" />
                  <TextField label="Foreman" value={editing.foreman ?? ''} onChange={(e) => set('foreman', e.target.value)} fullWidth size="small" />
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="outlined" color="success" onClick={async () => { await saveEdit(); }}>Save</Button>
          {editing?.reviewStatus !== 'VERIFIED' && (
            <Button variant="contained" color="success" onClick={async () => { await saveEdit(); await verify(editing); }}>
              Save & Verify
            </Button>
          )}
        </DialogActions>
      </AppDialog>

      <CustomerMergeDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        email={user?.email}
        onChanged={load}
      />
    </Box>
  );
}
